/* =========================================================================
   Work Orders — Worklist (v2)
   High-fidelity port of docs/featureBriefs/design_handoff_work_orders_change_orders/prototype/app/work-orders.js
   (PAGES["work-orders"]). See docs/featureBriefs/design_handoff_work_orders_change_orders/README.md §1.
   ========================================================================= */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./WorkOrders.css";
import {
    ClipboardList,
    RefreshCw,
    ArrowRight,
    Search,
    User,
    Filter,
    Download,
    FilePlus,
    Hash,
    Layers,
    ChevronUp,
    ChevronDown,
    DollarSign,
    AlertTriangle,
} from "lucide-react";
import { useSiteContext } from "../../../SiteContext";
import { can } from "../../../util/adminPermissions";
import { Page } from "../../../interfaces/PagesInterface";
import WorkOrdersManager from "../../../managers/WorkOrdersManager";
import ChangeOrdersManager from "../../../managers/ChangeOrdersManager";
import { downloadCombinedWorkOrderPdf, downloadWorkOrdersExcel } from "../../../api/workOrders";
import { WorkOrderListItem, WorkOrderStatus, WorkOrderTrade } from "../../../interfaces/WorkOrderInterface";
import { ChangeOrderListItem, ChangeOrderStatus } from "../../../interfaces/ChangeOrderInterface";
import {
    WO_STATUS_META,
    money,
    moneyCompact,
    fmtDate,
    WoNum,
    woNumText,
    StatusPill,
    TradeChip,
    AgePill,
    Ck,
    PageHead,
    SectionLabel,
    EmptyState,
    MultiSelect,
    ModalShell,
    Banner,
    DisabledNote,
    useToast,
} from "./woShared";

type StatusFilter = "all" | number;
type SortKey = "num" | "amount" | "age" | "status" | "proj";
type SortDir = "asc" | "desc";
type ModalState = { kind: "addToCO" } | { kind: "bulkPO" } | null;

const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
    { key: "num", label: "WO #" },
    { key: "amount", label: "Amount" },
    { key: "age", label: "Age" },
    { key: "status", label: "Status" },
    { key: "proj", label: "Project" },
];

const WorkOrdersPage: React.FC = () => {
    const { setPage, userRole } = useSiteContext();
    const canManageWorkOrders = can(userRole, "manageWorkOrders");
    const { toastNode, showToast } = useToast();

    // ── data ────────────────────────────────────────────────────────────────
    const [data, setData] = useState<WorkOrderListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const rows = await WorkOrdersManager.getWorkOrders();
            setData(Array.isArray(rows) ? rows : []);
        } catch (err) {
            console.error("Error fetching work orders:", err);
            setError("Failed to load work orders. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // ── filters / sort / selection state (persisted to WorkOrdersManager) ────
    const [status, setStatus] = useState<StatusFilter>(() => {
        const s = WorkOrdersManager.selectedStatus;
        return s && s > 0 ? s : "all";
    });
    const [trade, setTrade] = useState<"" | WorkOrderTrade>(
        () => (WorkOrdersManager.selectedTrade as "" | WorkOrderTrade) || ""
    );
    const [supIDs, setSupIDs] = useState<number[]>(() => [...WorkOrdersManager.selectedSupervisorIDs]);
    const [q, setQ] = useState("");
    const [sortKey, setSortKey] = useState<SortKey>("age");
    const [sortDir, setSortDir] = useState<SortDir>("desc");
    const [sel, setSel] = useState<Set<number>>(new Set());
    const [modal, setModal] = useState<ModalState>(null);

    useEffect(() => {
        // 0 is the "all" sentinel — WorkOrdersManager.selectedStatus is a plain
        // number field shared with the legacy list page, so "all" can't be
        // represented natively without touching the manager file.
        WorkOrdersManager.selectedStatus = typeof status === "number" ? status : 0;
    }, [status]);
    useEffect(() => {
        WorkOrdersManager.selectedTrade = trade;
    }, [trade]);
    useEffect(() => {
        WorkOrdersManager.selectedSupervisorIDs = supIDs;
    }, [supIDs]);

    const handleStatusFilter = (k: StatusFilter) => {
        setStatus(k);
        setSel(new Set());
    };

    // ── change-order membership ──────────────────────────────────────────────
    // The list endpoint resolves CO membership server-side (OUTER APPLY on the
    // junction table) into changeOrderID / changeOrderCORNumber.
    const isInChangeOrder = useCallback(
        (w: WorkOrderListItem) => w.changeOrderID != null || !!w.coNumber,
        []
    );

    // ── derived: counts / sums / supervisor options ──────────────────────────
    const counts = useMemo(() => {
        const c: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
        data.forEach((w) => {
            c[w.status] = (c[w.status] ?? 0) + 1;
        });
        return c;
    }, [data]);

    // TAS-557: every amount on this page is the amount due (total less any customer discount).
    const amountDue = (w: WorkOrderListItem): number => (w.totalAmount ?? 0) - (w.discountAmount ?? 0);

    const sums = useMemo(() => {
        const s: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
        data.forEach((w) => {
            s[w.status] = (s[w.status] ?? 0) + amountDue(w);
        });
        return s;
    }, [data]);

    const openAmount = useMemo(
        () =>
            data
                .filter((w) => w.status <= WorkOrderStatus.ReceivedPO)
                .reduce((a, w) => a + amountDue(w), 0),
        [data]
    );

    const supOptions = useMemo(() => {
        const named = new Map<number, string>();
        let hasUnassigned = false;
        data.forEach((w) => {
            if (w.wcdpSupervisorEmployeeID == null) hasUnassigned = true;
            else named.set(w.wcdpSupervisorEmployeeID, w.wcdpSupervisorName ?? "");
        });
        const opts = Array.from(named.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        if (hasUnassigned) opts.push({ id: 0, name: "Unassigned" });
        return opts;
    }, [data]);

    const toggleSup = (id: number | string) => {
        const numId = Number(id);
        setSupIDs((prev) => (prev.includes(numId) ? prev.filter((x) => x !== numId) : [...prev, numId]));
    };

    // ── filter + sort ─────────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return data.filter((w) => {
            if (status !== "all" && w.status !== status) return false;
            if (trade && w.trade !== trade) return false;
            if (supIDs.length && !supIDs.includes(w.wcdpSupervisorEmployeeID ?? 0)) return false;
            if (term) {
                const hay = [
                    w.altWorkOrderNumber,
                    String(w.workOrderID),
                    w.employeeName,
                    // Job # — the field office's primary handle on a work order. It is
                    // rendered on every row next to the project name, so it has to be
                    // searchable too.
                    w.altProjectID,
                    w.projectName,
                    w.contactName,
                    w.wcdpSupervisorName,
                    w.firstItemDescription,
                    w.purchaseOrderNumber,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });
    }, [data, status, trade, supIDs, q]);

    const rows = useMemo(() => {
        const arr = [...filtered];
        const dir = sortDir === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            switch (sortKey) {
                case "amount": {
                    const x = amountDue(a);
                    const y = amountDue(b);
                    return x > y ? dir : x < y ? -dir : 0;
                }
                case "age": {
                    const x = Date.parse(a.createdAt);
                    const y = Date.parse(b.createdAt);
                    return (x - y) * (sortDir === "asc" ? -1 : 1);
                }
                case "status": {
                    const x = a.status;
                    const y = b.status;
                    return x > y ? dir : x < y ? -dir : 0;
                }
                case "proj": {
                    const x = a.projectName;
                    const y = b.projectName;
                    return x > y ? dir : x < y ? -dir : 0;
                }
                default: {
                    const x = a.altWorkOrderNumber || String(a.workOrderID);
                    const y = b.altWorkOrderNumber || String(b.workOrderID);
                    return x > y ? dir : x < y ? -dir : 0;
                }
            }
        });
        return arr;
    }, [filtered, sortKey, sortDir]);

    const handleSort = (k: SortKey) => {
        if (sortKey === k) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(k);
            setSortDir(k === "age" ? "desc" : "asc");
        }
    };

    // ── selection ─────────────────────────────────────────────────────────────
    const toggleSel = (id: number) => {
        setSel((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const allSelected = rows.length > 0 && rows.every((w) => sel.has(w.workOrderID));
    const toggleSelAll = () => {
        setSel((prev) => {
            const next = new Set(prev);
            if (allSelected) rows.forEach((w) => next.delete(w.workOrderID));
            else rows.forEach((w) => next.add(w.workOrderID));
            return next;
        });
    };
    const clearSel = () => setSel(new Set());

    // Resolved against the full loaded dataset (not just the currently-filtered
    // rows) so the bulk bar always reflects everything the admin actually
    // checked, even after they narrow the filters further.
    const selectedItems = useMemo(() => data.filter((w) => sel.has(w.workOrderID)), [data, sel]);
    const selCount = selectedItems.length;
    const selTotal = selectedItems.reduce((a, w) => a + amountDue(w), 0);
    const allSelectedPendingPO = selCount > 0 && selectedItems.every((w) => w.status === WorkOrderStatus.PendingPO);

    // ── navigation ────────────────────────────────────────────────────────────
    const openDetail = (id: number) => {
        WorkOrdersManager.selectedWorkOrderID = id;
        setPage(Page.WorkOrderDetail);
    };
    const openChangeOrder = (changeOrderID: number) => {
        ChangeOrdersManager.selectedChangeOrderID = changeOrderID;
        setPage(Page.ChangeOrderDetail);
    };

    // ── bulk: Combined PDF ────────────────────────────────────────────────────
    const [pdfDownloading, setPdfDownloading] = useState(false);
    const [excelDownloading, setExcelDownloading] = useState(false);

    // TAS-307 — export exactly the rows on screen, in the same order.
    const handleExportExcel = async () => {
        if (!rows.length || excelDownloading) return;
        setExcelDownloading(true);
        try {
            await downloadWorkOrdersExcel(rows.map((w) => w.workOrderID));
        } catch {
            showToast("Export failed — try again.");
        } finally {
            setExcelDownloading(false);
        }
    };
    const handleCombinedPdf = async () => {
        if (!selectedItems.length) return;
        setPdfDownloading(true);
        try {
            await downloadCombinedWorkOrderPdf(selectedItems.map((w) => w.workOrderID));
            showToast("Combined PDF downloaded.");
        } catch (err) {
            console.error("Error downloading combined PDF:", err);
            showToast("Failed to download combined PDF. Please try again.");
        } finally {
            setPdfDownloading(false);
        }
    };

    // ── bulk: Add to Change Order ─────────────────────────────────────────────
    const [coOptions, setCoOptions] = useState<ChangeOrderListItem[]>([]);
    const [coOptionsLoading, setCoOptionsLoading] = useState(false);
    const [coSelection, setCoSelection] = useState("");
    const [coSubmitting, setCoSubmitting] = useState(false);

    const coEligibility = useMemo((): { ok: boolean; reason: string; job?: string } => {
        if (selectedItems.length === 0) return { ok: false, reason: "" };
        const job = selectedItems[0].altProjectID;
        if (!selectedItems.every((w) => w.altProjectID === job)) {
            return { ok: false, reason: "All selected work orders must be on the same job to add them to one change order." };
        }
        if (selectedItems.some((w) => isInChangeOrder(w))) {
            return { ok: false, reason: "One or more selected work orders are already in a change order." };
        }
        if (selectedItems.some((w) => w.status >= WorkOrderStatus.Voided)) {
            return { ok: false, reason: "Voided or No Charge work orders can't be added to a change order." };
        }
        return { ok: true, reason: "", job };
    }, [selectedItems, isInChangeOrder]);

    const openAddToCO = () => {
        setCoSelection("");
        setModal({ kind: "addToCO" });
    };

    useEffect(() => {
        if (modal?.kind !== "addToCO" || !coEligibility.ok || !coEligibility.job) return;
        let cancelled = false;
        setCoOptionsLoading(true);
        ChangeOrdersManager.getChangeOrders(ChangeOrderStatus.Open)
            .then((list: ChangeOrderListItem[]) => {
                if (cancelled) return;
                setCoOptions((list || []).filter((c) => c.jobNumber === coEligibility.job));
            })
            .catch((err: unknown) => {
                console.error("Error fetching eligible change orders:", err);
                if (!cancelled) setCoOptions([]);
            })
            .finally(() => {
                if (!cancelled) setCoOptionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal, coEligibility.ok, coEligibility.job]);

    const confirmAddToCO = async () => {
        if (coSelection === "new") {
            ChangeOrdersManager.selectedJobNumber = coEligibility.job ?? "";
            setModal(null);
            setPage(Page.ChangeOrderCreate);
            return;
        }
        const changeOrderID = Number(coSelection);
        if (!changeOrderID) return;
        setCoSubmitting(true);
        try {
            await ChangeOrdersManager.addWorkOrders(changeOrderID, selectedItems.map((w) => w.workOrderID));
            setModal(null);
            showToast("Work orders added to change order.");
            clearSel();
            fetchAll();
        } catch (err) {
            console.error("Error adding work orders to change order:", err);
            showToast("Failed to add work orders to the change order. Please try again.");
        } finally {
            setCoSubmitting(false);
        }
    };

    // ── bulk: Add PO# ─────────────────────────────────────────────────────────
    const [poNumber, setPoNumber] = useState("");
    const [poRunning, setPoRunning] = useState(false);
    const [poProgress, setPoProgress] = useState(0);
    const [poFailed, setPoFailed] = useState<string[]>([]);

    const openBulkPO = () => {
        setPoNumber("");
        setPoRunning(false);
        setPoProgress(0);
        setPoFailed([]);
        setModal({ kind: "bulkPO" });
    };

    const runBulkPO = async () => {
        const trimmed = poNumber.trim();
        if (!trimmed || !selectedItems.length) return;
        setPoRunning(true);
        setPoFailed([]);
        const failed: string[] = [];
        for (let i = 0; i < selectedItems.length; i++) {
            const w = selectedItems[i];
            try {
                await WorkOrdersManager.setPONumber(w.workOrderID, trimmed);
            } catch (err) {
                console.error("Error setting PO number on work order:", w.workOrderID, err);
                failed.push(woNumText(w));
            }
            setPoProgress(i + 1);
        }
        setPoRunning(false);
        if (failed.length) {
            setPoFailed(failed);
            return; // keep the modal open so the admin can see/retry
        }
        setModal(null);
        showToast(`PO# applied to ${selectedItems.length} work order${selectedItems.length === 1 ? "" : "s"} — advanced to Received PO.`);
        clearSel();
        fetchAll();
    };

    // ── render: loading / error ───────────────────────────────────────────────
    if (loading) {
        return (
            <div className="wov2">
                <PageHead
                    Icon={ClipboardList}
                    title="Work Orders"
                    sub="Every field work order and where it stands. One list — filter by status, trade or supervisor."
                />
                <SectionLabel Icon={RefreshCw} style={{ margin: "6px 0 12px" }}>
                    Work order lifecycle
                </SectionLabel>
                <div className="card pad" style={{ textAlign: "center", color: "var(--text-3)" }}>
                    Loading work orders…
                </div>
                {toastNode}
            </div>
        );
    }

    if (error) {
        return (
            <div className="wov2">
                <PageHead
                    Icon={ClipboardList}
                    title="Work Orders"
                    sub="Every field work order and where it stands. One list — filter by status, trade or supervisor."
                />
                <Banner
                    variant="danger"
                    Icon={AlertTriangle}
                    action={
                        <button type="button" className="btn sm" onClick={fetchAll}>
                            Retry
                        </button>
                    }
                >
                    {error}
                </Banner>
                {toastNode}
            </div>
        );
    }

    // ── render: page ──────────────────────────────────────────────────────────
    return (
        <div className="wov2">
            <PageHead
                Icon={ClipboardList}
                title="Work Orders"
                sub="Every field work order and where it stands. One list — filter by status, trade or supervisor."
                actions={
                    <>
                        <button
                            type="button"
                            className="btn sm"
                            onClick={handleExportExcel}
                            disabled={excelDownloading || rows.length === 0}
                            title="Export the current filtered list to Excel"
                        >
                            <Download size={14} />
                            {excelDownloading ? "Exporting…" : `Export Excel (${rows.length})`}
                        </button>
                        <span className="tag" title="Open + in-progress value">
                            <DollarSign size={12} />
                            {money(openAmount)} open
                        </span>
                    </>
                }
            />

            <SectionLabel Icon={RefreshCw} style={{ margin: "6px 0 12px" }}>
                Work order lifecycle
            </SectionLabel>

            <div className="tr-flow wo-flow">
                {[1, 2, 3, 4].map((s) => {
                    const meta = WO_STATUS_META[s];
                    const n = counts[s] ?? 0;
                    const on = status === s;
                    return (
                        <button
                            key={s}
                            type="button"
                            className={`tr-stage ${meta.stClass}${on ? " on" : ""}${n === 0 ? " zero" : ""}`}
                            onClick={() => handleStatusFilter(s)}
                        >
                            <span className="fs-top">
                                <span className="fdot" style={{ background: meta.color }} />
                                {meta.short}
                            </span>
                            <span className="fs-count">{n}</span>
                            <span className="fs-sub">{n ? `${moneyCompact(sums[s] ?? 0)} · ${meta.note}` : "nothing here"}</span>
                            <span className="fs-arrow">
                                <ArrowRight />
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="wo-offramp">
                <button type="button" className={`wo-ort${status === "all" ? " on" : ""}`} onClick={() => handleStatusFilter("all")}>
                    <ClipboardList size={15} />
                    All work orders
                    <b>{data.length}</b>
                </button>
                <span className="wo-orsep">Off-pipeline</span>
                <button
                    type="button"
                    className={`wo-ort${status === WorkOrderStatus.NoCharge ? " on" : ""}`}
                    onClick={() => handleStatusFilter(WorkOrderStatus.NoCharge)}
                >
                    <span className="fdot" style={{ background: WO_STATUS_META[WorkOrderStatus.NoCharge].color }} />
                    No Charge
                    <b>{counts[WorkOrderStatus.NoCharge] ?? 0}</b>
                </button>
                <button
                    type="button"
                    className={`wo-ort${status === WorkOrderStatus.Voided ? " on" : ""}`}
                    onClick={() => handleStatusFilter(WorkOrderStatus.Voided)}
                >
                    <span className="fdot" style={{ background: WO_STATUS_META[WorkOrderStatus.Voided].color }} />
                    Voided
                    <b>{counts[WorkOrderStatus.Voided] ?? 0}</b>
                </button>
            </div>

            <div className="toolbar" style={{ marginTop: 18 }}>
                <div className="searchwrap">
                    <Search />
                    <input
                        type="text"
                        placeholder="Search job #, WO #, employee, project, contact, PO…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
                <MultiSelect Icon={User} label="Supervisor" options={supOptions} selected={supIDs} onToggle={toggleSup} />
                <span className="inline-field">
                    <Filter size={14} />
                    <select className="select" value={trade} onChange={(e) => setTrade(e.target.value as "" | WorkOrderTrade)}>
                        <option value="">All trades</option>
                        <option value="Drywall">Drywall</option>
                        <option value="Paint">Paint</option>
                        <option value="Other">Other</option>
                    </select>
                </span>
            </div>

            {selCount > 0 && (
                <div className="selbar">
                    <span className="cnt">{selCount} selected</span>
                    <span className="muted" style={{ fontSize: 12.5 }}>
                        {money(selTotal)} total
                    </span>
                    <span className="grow" />
                    <div className="actions">
                        <button type="button" className="btn sm" onClick={handleCombinedPdf} disabled={pdfDownloading}>
                            <Download size={14} />
                            {pdfDownloading ? "Preparing…" : "Combined PDF"}
                        </button>
                        {canManageWorkOrders && (
                            <button type="button" className="btn sm" onClick={openAddToCO}>
                                <FilePlus size={14} />
                                Add to Change Order
                            </button>
                        )}
                        {canManageWorkOrders && (
                            <button
                                type="button"
                                className={`btn sm${allSelectedPendingPO ? "" : " disabled"}`}
                                disabled={!allSelectedPendingPO}
                                onClick={openBulkPO}
                                title={allSelectedPendingPO ? "" : "Only Pending PO work orders can take a PO#"}
                            >
                                <Hash size={14} />
                                Bulk add PO#
                            </button>
                        )}
                        <button type="button" className="btn sm ghost" onClick={clearSel}>
                            Clear
                        </button>
                    </div>
                </div>
            )}

            <div className="card">
                <div className="wo-lbar">
                    <Ck on={allSelected} onClick={toggleSelAll} title="Select all" />
                    <span className="wo-lbar-lbl">Sort</span>
                    {SORT_OPTIONS.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`wo-sortb${sortKey === key ? " on" : ""}`}
                            onClick={() => handleSort(key)}
                        >
                            {label}
                            {sortKey === key ? sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} /> : null}
                        </button>
                    ))}
                    <span className="grow" />
                    <span className="muted" style={{ fontSize: 12.5 }}>
                        Showing <b>{rows.length}</b> of {data.length}
                    </span>
                </div>

                {rows.length ? (
                    <div className="wo-list">
                        {rows.map((w) => {
                            const coLink =
                                w.changeOrderID != null && w.changeOrderCORNumber != null
                                    ? { changeOrderID: w.changeOrderID, corNumber: w.changeOrderCORNumber }
                                    : null;
                            return (
                                <div
                                    key={w.workOrderID}
                                    className="wo-row"
                                    tabIndex={0}
                                    role="button"
                                    onClick={() => openDetail(w.workOrderID)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            openDetail(w.workOrderID);
                                        }
                                    }}
                                >
                                    <Ck
                                        on={sel.has(w.workOrderID)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSel(w.workOrderID);
                                        }}
                                    />
                                    <div className="wo-main">
                                        <div className="wo-l1">
                                            <WoNum wo={w} />
                                            <span className="wo-emp">{w.employeeName}</span>
                                            <StatusPill kind="wo" status={w.status} />
                                            {w.billingOnly && (
                                                <span className="tag" title="Billing only — no time claims">Billing only</span>
                                            )}
                                        </div>
                                        <div className="wo-meta">
                                            <span className="m">
                                                <span className="mlbl">Project</span>
                                                {w.projectName} <span className="pcode">{w.altProjectID}</span>
                                            </span>
                                            <span className="m">
                                                <span className="mlbl">Contact</span>
                                                {w.contactName || <span className="dash">–</span>}
                                            </span>
                                            <span className="m">
                                                <span className="mlbl">Supervisor</span>
                                                {w.wcdpSupervisorName || "Unassigned"}
                                            </span>
                                            <span className="m">
                                                <span className="mlbl">Trade</span>
                                                <TradeChip trade={w.trade} />
                                            </span>
                                            <span className="m">
                                                <span className="mlbl">PO #</span>
                                                {w.purchaseOrderNumber ? (
                                                    <span className="code">{w.purchaseOrderNumber}</span>
                                                ) : (
                                                    <span className="dash">–</span>
                                                )}
                                            </span>
                                            <span className="m">
                                                <span className="mlbl">Age</span>
                                                <AgePill createdAt={w.createdAt} /> <span className="muted">{fmtDate(w.createdAt)}</span>
                                            </span>
                                            {coLink && (
                                                <span className="m" onClick={(e) => e.stopPropagation()}>
                                                    <span className="mlbl">Change Order</span>
                                                    <button
                                                        type="button"
                                                        className="tag"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openChangeOrder(coLink.changeOrderID);
                                                        }}
                                                    >
                                                        <Layers size={12} />
                                                        COR #{coLink.corNumber}
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="wo-right">
                                        <span className="wo-amt">{money(amountDue(w))}</span>
                                        <ArrowRight className="wo-chev" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <EmptyState Icon={ClipboardList} title="No work orders found" style={{ padding: "48px 24px" }}>
                        Try a different status stage, or clear the trade / supervisor / search filters.
                    </EmptyState>
                )}
            </div>

            {modal?.kind === "addToCO" && (
                <ModalShell
                    Icon={FilePlus}
                    title={`Add ${selCount} work order${selCount === 1 ? "" : "s"} to a Change Order`}
                    sub="Only open change orders on the same job are eligible"
                    onClose={() => setModal(null)}
                    footer={
                        <>
                            <button type="button" className="btn ghost" onClick={() => setModal(null)}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn primary"
                                disabled={!coEligibility.ok || !coSelection || coSubmitting}
                                onClick={confirmAddToCO}
                            >
                                {coSubmitting ? "Adding…" : "Add to change order"}
                            </button>
                        </>
                    }
                >
                    {!coEligibility.ok ? (
                        <DisabledNote>{coEligibility.reason}</DisabledNote>
                    ) : (
                        <div className="field">
                            <label>Change Order</label>
                            <select className="select" value={coSelection} onChange={(e) => setCoSelection(e.target.value)}>
                                <option value="" disabled>
                                    {coOptionsLoading ? "Loading…" : "Select a change order…"}
                                </option>
                                {coOptions.map((c) => (
                                    <option key={c.changeOrderID} value={String(c.changeOrderID)}>
                                        COR #{c.corNumber} · Job #{c.jobNumber} · {c.projectName}
                                    </option>
                                ))}
                                <option value="new">+ Create new change order…</option>
                            </select>
                        </div>
                    )}
                </ModalShell>
            )}

            {modal?.kind === "bulkPO" && (
                <ModalShell
                    Icon={Hash}
                    title={`Add PO# to ${selCount} work orders`}
                    sub="Applies to every selected Pending PO"
                    onClose={() => !poRunning && setModal(null)}
                    footer={
                        <>
                            <button type="button" className="btn ghost" onClick={() => setModal(null)} disabled={poRunning}>
                                Cancel
                            </button>
                            <button type="button" className="btn primary" disabled={!poNumber.trim() || poRunning} onClick={runBulkPO}>
                                {poRunning ? `Applying ${poProgress}/${selCount}…` : `Apply to ${selCount}`}
                            </button>
                        </>
                    }
                >
                    <Banner variant="info" Icon={RefreshCw}>
                        All {selCount} selected work orders advance to <b>Received PO</b> and their PDFs regenerate.
                    </Banner>
                    <div className="field" style={{ marginTop: 14 }}>
                        <label>Purchase Order Number</label>
                        <input
                            className="input"
                            placeholder="e.g. PO-44120"
                            value={poNumber}
                            onChange={(e) => setPoNumber(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {poFailed.length > 0 && (
                        <div style={{ marginTop: 14 }}>
                            <Banner variant="danger" Icon={AlertTriangle}>
                                Failed to apply PO# to: {poFailed.join(", ")}. Fix and try again.
                            </Banner>
                        </div>
                    )}
                </ModalShell>
            )}

            {toastNode}
        </div>
    );
};

export default WorkOrdersPage;
